import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import styled from "styled-components";
import Sidebar, { SidebarMenuItem } from "components/molecules/Sidebar";
import AdminRoute from "components/common/AdminRoute";
import { withPageGuard } from "components/common/withPageGuard";
import { NavGuardContext, useNavGuardSlot } from "./NavGuardContext";

// Admin 전용 메뉴 항목
export const adminMenuItems: SidebarMenuItem[] = [
  {
    id: "dashboard",
    label: "홈으로",
    path: "/dashboard",
    icon: "Home",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "medications",
    label: "약물 관리",
    path: "/admin/medications",
    icon: "Media",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "health-functional-foods",
    label: "건기식 관리",
    path: "/admin/health-functional-foods",
    icon: "Media",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "prompts",
    label: "프롬프트 관리",
    path: "/admin/prompts",
    icon: "Text",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "users",
    label: "사용자 관리",
    path: "/admin/users",
    icon: "User Plus",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "premium-licenses",
    label: "프리미엄 면허번호 관리",
    path: "/admin/premium-licenses",
    icon: "ID Card",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "subscriptions",
    label: "유료 프리미엄 관리",
    path: "/admin/subscriptions",
    icon: "Won",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "pharmacy-access",
    label: "PhAI 약국 가입 관리",
    path: "/admin/pharmacy-access",
    icon: "Building Filled",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "ai-reviews",
    label: "AI 리뷰 관리",
    path: "/admin/ai-reviews",
    icon: "Document",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "ai-review-feedbacks",
    label: "AI 리뷰 피드백 관리",
    path: "/admin/feedbacks",
    icon: "Star",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "ai-review-test-gt",
    label: "AI 평가 GT 관리",
    path: "/admin/ai-review-test/ground-truths",
    icon: "Check Verified",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "ai-review-test-results",
    label: "AI 평가 결과",
    path: "/admin/ai-review-test/results",
    icon: "Check Circle Filled",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "ai-review-test-analysis",
    label: "AI 평가 분석",
    path: "/admin/ai-review-test/analysis",
    icon: "Dashboard",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "ai-review-statistics",
    label: "AI 리뷰 통계",
    path: "/admin/statistics/ai-reviews",
    icon: "List Number",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "drug-identifications",
    label: "의약품 식별 이력",
    path: "/admin/drug-identifications",
    icon: "Camera",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "consultation-records",
    label: "상담 녹음 기록",
    path: "/admin/consultation-records",
    icon: "Microphone",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "dose-rules",
    label: "용량 룰 검수",
    path: "/admin/dose-rules",
    icon: "Clipboard Check",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "dose-review-logs",
    label: "판정 기록",
    path: "/admin/dose-review-logs",
    icon: "Clipboard Search",
    requireAuth: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    id: "partners",
    label: "외부 연동",
    path: "/admin/partners",
    icon: "Link",
    requireAuth: true,
    requireAdmin: true,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
];

interface AdminLayoutProps {
  children?: React.ReactNode;
  menuItems?: SidebarMenuItem[];
}

// 레이아웃 컨테이너 스타일
const LayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
`;

// 메인 콘텐츠 영역 스타일
const MainContent = styled.div<{ sidebarOpen: boolean }>`
  flex: 1;
  padding: 24px;
  margin-left: ${({ sidebarOpen }) => (sidebarOpen ? "250px" : "60px")};
  transition: margin-left 0.3s ease;
  overflow-y: auto;
`;

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, menuItems = adminMenuItems }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // 상세가 등록한 이동 가드를 Sidebar로 넘긴다(D-19). 가드 없으면 check가 true라 동작 불변.
  const navGuard = useNavGuardSlot();

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <AdminRoute>
      <NavGuardContext.Provider value={navGuard}>
        <LayoutContainer>
          <Sidebar
            items={menuItems}
            isOpen={sidebarOpen}
            onToggle={handleSidebarToggle}
            onBeforeNavigate={navGuard.check}
          />
          <MainContent sidebarOpen={sidebarOpen}>{children || <Outlet />}</MainContent>
        </LayoutContainer>
      </NavGuardContext.Provider>
    </AdminRoute>
  );
};

// 관리자 레이아웃은 로그인, 약관 동의, 관리자 권한이 모두 필요
export default withPageGuard(AdminLayout, {
  requireAuth: true,
  requireGuide: true,
  requireAgreement: true,
  requireAdmin: true,
});
